import React from 'react';
import { PlusIcon, EllipsisVerticalIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link } from 'react-router-dom';
import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import StatusBadge from '@dashboard/components/StatusBadge';
import Button from '@dashboard/components/Button';
import { formatPrice } from '@/dashboard/services/formatting';
import Tooltip from '@/dashboard/components/Tooltip';
import checkIsEventSoldOut from '@dashboard/services/event/checkIsEventSoldOut';
import checkIsTicketGroupSoldOut from '@repo/checkout/lib/checkIsTicketGroupSoldOut';
import TicketTypeSaleDate from '@repo/checkout/components/TicketTypeSaleDate';
import checkIsTicketTypeScheduled from '@repo/checkout/lib/checkIsTicketTypeScheduled';
import checkIsTicketGroupScheduled from '@repo/checkout/lib/checkIsTicketGroupScheduled';
import checkIsTicketTypeOnSale from '@dashboard/services/event/checkIsTicketTypeOnSale';
import checkIsTicketGroupOnSale from '@dashboard/services/event/checkIsTicketGroupOnSale';
import TicketGroupEventOverviewBadges from '../../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/components/TicketGroupEventOverviewBadges';
import {
  TicketGroupAvailabilityStatus,
  TicketTypeAvailabilityStatus,
} from '@/dashboard/enums/tickets';
import checkIsTicketTypeSoldOut from '@repo/checkout/lib/checkIsTicketTypeSoldOut';
import { shouldShowTicketSaleDates } from '@repo/checkout/lib/ticketAvailabilityHelpers';
import checkIsTicketTypeNotOnSale from '@repo/checkout/lib/checkIsTicketTypeNotOnSale';
import checkIsTicketGroupNotOnSale from '@repo/checkout/lib/checkIsTicketGroupNotOnSale';

function EventTicketSalesTableRow({ ticket, pageId, isGroup, ticketGroup, page }) {
  const queryKey = isGroup ? 'ticketGroup' : 'ticketType';

  const viewPath = `/pages/${pageId}/guests?${queryKey}=${ticket._id}`;
  const editPath = `/pages/${pageId}/customize/event/ticket-types`;

  const isTicketGroupSoldOut = checkIsTicketGroupSoldOut(ticketGroup);
  const isTicketTypeSoldOut = checkIsTicketTypeSoldOut(ticket);
  const isSoldOut = checkIsEventSoldOut(page) || isTicketGroupSoldOut || isTicketTypeSoldOut;

  const isCanceled = page.eventDetails?.status === 'canceled';

  const isTicketTypeUnavailable =
    ticket.availabilityStatus === TicketTypeAvailabilityStatus.UNAVAILABLE;
  const isTicketGroupUnavailable =
    ticketGroup?.availabilityStatus === TicketGroupAvailabilityStatus.UNAVAILABLE;
  const isUnavailable = isTicketGroupUnavailable || isTicketTypeUnavailable;

  const allTicketTypes = page.ticketGroups.map(group => group.ticketTypes).flat();

  const ticketTypeScheduleResult = checkIsTicketTypeScheduled({
    ticketType: ticket,
    allTicketTypes,
  });
  const ticketGroupScheduleResult = checkIsTicketGroupScheduled({
    ticketGroup,
    allTicketGroups: page.ticketGroups,
  });

  const isScheduled = ticketTypeScheduleResult.isScheduled || ticketGroupScheduleResult.isScheduled;
  const hasInvalidReference =
    ticketTypeScheduleResult.hasInvalidReference || ticketGroupScheduleResult.hasInvalidReference;

  const isTicketGroupOnSale = checkIsTicketGroupOnSale({
    page,
    ticketGroup,
    allTicketGroups: page.ticketGroups,
  });

  const isTicketTypeOnSale = checkIsTicketTypeOnSale({
    page,
    ticketType: ticket,
    allTicketTypes,
  });

  const isOnSale = isTicketTypeOnSale && isTicketGroupOnSale;

  const isTicketTypeNotOnSale = checkIsTicketTypeNotOnSale({
    ticketType: ticket,
    isCanceled,
    hasInvalidReference: ticketTypeScheduleResult.hasInvalidReference,
    isOnSale: isTicketTypeOnSale,
    isScheduled: ticketTypeScheduleResult.isScheduled,
  });

  const isGroupNotOnSale = checkIsTicketGroupNotOnSale({
    ticketGroup,
    isCanceled,
    hasInvalidReference: ticketGroupScheduleResult.hasInvalidReference,
    isOnSale: isTicketGroupOnSale,
    isScheduled: ticketGroupScheduleResult.isScheduled,
  });

  const isNotOnSale = isTicketTypeNotOnSale || isGroupNotOnSale;

  const showTicketSaleDates = shouldShowTicketSaleDates(
    ticket,
    ticketGroup,
    isCanceled,
    isSoldOut,
    isUnavailable,
    isOnSale,
  );

  const isEventSoldOut = checkIsEventSoldOut(page);

  const showTicketTypeHiddenBadge =
    ticket.status === 'enabled' &&
    (ticket.hidden === true ||
      ticketGroup.hidden === true ||
      (ticket.hideWhenNotOnSale && isTicketTypeNotOnSale) ||
      (ticketGroup.hideWhenNotOnSale && isNotOnSale) ||
      (ticket.hideWhenScheduled && ticketTypeScheduleResult.isScheduled) ||
      (ticketGroup.hideWhenScheduled && isScheduled) ||
      (ticket.hideWhenUnavailable && isTicketTypeUnavailable) ||
      (ticketGroup.hideWhenUnavailable && isTicketGroupUnavailable) ||
      (ticket.hideWhenSoldOut && isTicketTypeSoldOut) ||
      (ticketGroup.hideWhenSoldOut && isTicketGroupSoldOut));

  return (
    <CardListItem
      href={`/pages/${pageId}/guests?${queryKey}=${ticket._id}`}
      className="mb-1 border rounded-sm border-gray-100"
    >
      <CardListItemElement size="fill" align="right">
        <div className="gap-y-1">
          {isGroup && <div className="text-sm text-gray-500">Group</div>}
          <div className="flex items-center gap-2">
            <div className="font-medium">{ticket.name}</div>
          </div>

          {!isGroup && (
            <>
              <div className="text-sm">
                {ticket.price === 0
                  ? 'Free'
                  : formatPrice({
                      locale: page.locale,
                      amount: ticket.price,
                      currency: page.eventDetails?.currency || ticket.currency,
                    })}
              </div>

              {showTicketSaleDates && (
                <div className="text-gray-700 text-sm">
                  <TicketTypeSaleDate
                    page={page}
                    ticketType={ticket}
                    ticketGroup={ticketGroup}
                    allTicketTypes={ticketGroup?.ticketTypes ?? []}
                    allTicketGroups={page.ticketGroups ?? []}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </CardListItemElement>

      <CardListItemElement size="large" className="!py-0">
        <div className="font-medium">
          {formatPrice({
            locale: page.locale,
            amount: ticket.revenue.totalRevenue || 0,
            currency: page.eventDetails?.currency || ticket.currency,
          })}
        </div>
      </CardListItemElement>
      <CardListItemElement size="medium">
        <span>
          {ticket.quantitySold ?? 0}
          {ticket.capacity != null && `/${ticket.capacity}`}
        </span>
      </CardListItemElement>
      <CardListItemElement size="large">
        <div>
          <div className="font-medium">
            {ticket.checkedInCount || 0}/{ticket.quantitySold}
          </div>
        </div>
      </CardListItemElement>
      <CardListItemElement size="large" className="flex justify-between">
        <div className="flex items-center">
          <div className="flex flex-col items-start">
            {isGroup ? (
              <TicketGroupEventOverviewBadges
                page={page}
                ticketGroup={ticketGroup}
                allTicketGroups={page.ticketGroups}
              />
            ) : (
              <>
                {ticket.status === 'disabled' ? (
                  <Tooltip label="Disabled tickets will not be shown to customers.">
                    <StatusBadge status="disabled" label="Disabled" />
                  </Tooltip>
                ) : ticket.status === 'archived' ? (
                  <Tooltip label="This ticket is shown as archived to customers.">
                    <StatusBadge status="archived" label="Archived" />
                  </Tooltip>
                ) : isCanceled ? (
                  <Tooltip label="This event has been canceled.">
                    <StatusBadge status="canceled" label="Canceled" />
                  </Tooltip>
                ) : isEventSoldOut ? (
                  <Tooltip label="This ticket no longer has availability.">
                    <StatusBadge status="sold_out" label="Sold out" />
                  </Tooltip>
                ) : ticketGroup.availabilityStatus === TicketGroupAvailabilityStatus.UNAVAILABLE ? (
                  <Tooltip label="This ticket is shown as unavailable to customers.">
                    <StatusBadge status="unavailable" label="Unavailable" />
                  </Tooltip>
                ) : isGroupNotOnSale ? (
                  <Tooltip label="This ticket is currently not on sale..">
                    <StatusBadge status="sale_ended" label="Not on sale" />
                  </Tooltip>
                ) : isSoldOut ? (
                  <Tooltip label="This ticket no longer has availability.">
                    <StatusBadge status="sold_out" label="Sold out" />
                  </Tooltip>
                ) : isUnavailable ? (
                  <Tooltip label="This ticket is shown as unavailable to customers.">
                    <StatusBadge status="unavailable" label="Unavailable" />
                  </Tooltip>
                ) : isNotOnSale || hasInvalidReference || isTicketTypeNotOnSale ? (
                  <Tooltip label="This ticket is currently not on sale..">
                    <StatusBadge status="sale_ended" label="Not on sale" />
                  </Tooltip>
                ) : isOnSale ? (
                  <Tooltip label="This ticket is on sale.">
                    <StatusBadge status="on_sale" label="On sale" />
                  </Tooltip>
                ) : isScheduled ? (
                  <Tooltip label="This ticket is scheduled to go on sale.">
                    <StatusBadge status="scheduled" label="On sale soon" />
                  </Tooltip>
                ) : (
                  <Tooltip label="This ticket is on sale.">
                    <StatusBadge status="on_sale" label="On sale" />
                  </Tooltip>
                )}

                {showTicketTypeHiddenBadge && (
                  <Tooltip label="This ticket is hidden.">
                    <StatusBadge status="hidden" label="Hidden" />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button appearance="icon-button" className="mr-2">
              <EllipsisVerticalIcon className="w-4 h-4" />
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="origin-top-right absolute right-0 z-20 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
              side="bottom"
              align="end"
            >
              <DropdownMenu.Item asChild>
                <Link
                  to={editPath}
                  className="w-full flex p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900 !focus:ring-0 !focus:outline-none"
                >
                  Edit
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link
                  to={viewPath}
                  className="w-full flex p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900 !focus:ring-0 !focus:outline-none"
                >
                  View guests
                </Link>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </CardListItemElement>
    </CardListItem>
  );
}

function EventTicketSales({ ticketGroups, page }) {
  const pageWithTicketGroups = { ...page, ticketGroups };
  const getGroupStats = ticketGroup => {
    const totalPrice = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalOriginalPrice || 0);
    }, 0);

    const totalDiscountAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalDiscountAmount || 0);
    }, 0);

    const totalFeeAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalFeeAmount || 0);
    }, 0);

    const totalTaxAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalTaxAmount || 0);
    }, 0);

    const totalCouponAmount = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalCouponAmount || 0);
    }, 0);

    const totalRevenue = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalRevenue || 0);
    }, 0);

    const totalSold = ticketGroup.ticketTypes.reduce((sum, ticket) => sum + ticket.quantitySold, 0);

    const totalCapacity = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.capacity || 0);
    }, 0);

    const totalRemaining = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.capacity != null ? ticket.capacity - ticket.quantitySold : 0);
    }, 0);

    const totalCheckedIn = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.checkedInCount || 0);
    }, 0);

    const totalOriginalPrice = ticketGroup.ticketTypes.reduce((sum, ticket) => {
      return sum + (ticket.revenue.totalOriginalPrice || 0);
    }, 0);

    return {
      totalOriginalPrice,
      totalRevenue,
      totalPrice,
      totalDiscountAmount,
      totalFeeAmount,
      totalTaxAmount,
      totalCouponAmount,
      totalSold,
      totalCapacity,
      totalRemaining,
      totalCheckedIn,
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-2 justify-end">
        <div className="flex gap-2">
          <Button
            to={`/pages/${page._id}/customize/event/ticket-types?openAddTicketGroup=true`}
            appearance="secondary"
            size="small"
            className="inline-flex items-center gap-1.5"
          >
            <PlusIcon className="w-4 h-4" />
            Add groups
          </Button>
          <Button
            to={`/pages/${page._id}/customize/event/ticket-types`}
            appearance="secondary"
            size="small"
            className="inline-flex items-center gap-1.5"
          >
            <PlusIcon className="w-4 h-4" />
            Add tickets
          </Button>
        </div>
      </div>

      {ticketGroups
        .filter(ticketGroup => ticketGroup.ticketTypes.length > 0)
        .map((ticketGroup, index) => {
          const groupStats = getGroupStats(ticketGroup);

          return (
            <div key={ticketGroup._id}>
              <CardContent className="overflow-auto">
                <CardListItem variant="header" className="!bg-white !border-0  pb-2 !mt-2">
                  <CardListItemElement size="fill" align="left" className="!py-0">
                    {index === 0 && (
                      <div className="font-medium text-gray-900 text-xl !ml-0 !pl-0">
                        Ticket sales
                      </div>
                    )}
                  </CardListItemElement>

                  <CardListItemElement size="large" className="!py-0">
                    <div className="font-medium text-gray-900">
                      Revenue
                      <Tooltip
                        label="Gross revenue after discounts, fees, and taxes — all applied proportionally,
                      before refunds"
                      >
                        <span type="button" className="inline-block align-sub ml-1">
                          <InformationCircleIcon className="w-4 h-4 text-gray-600 hover:text-gray-900 transition-colors" />
                        </span>
                      </Tooltip>
                    </div>
                  </CardListItemElement>

                  <CardListItemElement size="medium" className="!py-0">
                    <div className="font-medium text-gray-900 flex items-center gap-1">Sold</div>
                  </CardListItemElement>
                  <CardListItemElement size="large" className="!py-0">
                    <div className="font-medium text-gray-900">Checked in</div>
                  </CardListItemElement>
                  <CardListItemElement size="large" className="!py-0">
                    <div className="font-medium text-gray-900 flex items-center gap-1">Status</div>
                  </CardListItemElement>
                </CardListItem>

                <EventTicketSalesTableRow
                  ticket={{
                    ...ticketGroup,
                    name: ticketGroup.name,
                    revenue: {
                      ...groupStats,
                    },
                    totalRevenue: groupStats.totalRevenue,
                    status: ticketGroup.status,
                    quantitySold: groupStats.totalSold,
                    checkedInCount: groupStats.totalCheckedIn,
                    capacity: ticketGroup.capacity,
                    hidden: ticketGroup.hidden,
                  }}
                  isGroup={true}
                  pageId={page._id}
                  ticketGroup={ticketGroup}
                  page={pageWithTicketGroups}
                />

                {ticketGroup.ticketTypes.map(ticket => (
                  <EventTicketSalesTableRow
                    key={ticket._id}
                    ticket={{
                      ...ticket,
                      status: ticketGroup.status === 'disabled' ? 'disabled' : ticket.status,
                    }}
                    pageId={page._id}
                    ticketGroup={ticketGroup}
                    page={pageWithTicketGroups}
                  />
                ))}
              </CardContent>
            </div>
          );
        })}
    </div>
  );
}

export default EventTicketSales;
